/**
 * Demo Data Service
 * Provides realistic simulated data for all POL dashboard API endpoints.
 * Tampa Bay, FL location theme. Used by demoMode.js interceptors.
 *
 * Three demo profiles:
 *   1003 - Amara Okafor   → LOW risk  (compliant, stable routine)
 *   1001 - Priya Sharma   → MEDIUM risk (some concerns, mostly compliant)
 *   1002 - David Chen     → HIGH risk (non-compliant, erratic behavior)
 */

// ============================================================================
// DEMO LOCATIONS (Tampa Bay, FL)
// ============================================================================
const DEMO_LOCATIONS = {
  home: { lat: 27.9506, lon: -82.4572, name: 'Home', type: 'home' },
  work: { lat: 27.9478, lon: -82.4584, name: 'Workplace - Downtown Tampa', type: 'work' },
  gym: { lat: 27.9623, lon: -82.4631, name: 'Tampa Fitness Center', type: 'frequent' },
  grocery: { lat: 27.9445, lon: -82.4510, name: 'Publix Supermarket', type: 'frequent' },
  restaurant: { lat: 27.9530, lon: -82.4615, name: 'Ybor City Restaurant', type: 'frequent' },
  friend: { lat: 27.9701, lon: -82.4487, name: "Friend's House - Seminole Heights", type: 'frequent' },
};

const now = () => new Date().toISOString();
const hoursAgo = (h) => new Date(Date.now() - h * 3600000).toISOString();
const daysAgo = (d) => new Date(Date.now() - d * 86400000).toISOString();

/** Profile mapping: determines risk tier for each demo person */
const _profile = (id) => {
  const n = Number(id);
  if (n === 1003) return 'low';
  if (n === 1002) return 'high';
  return 'medium'; // 1001 and any other ID
};

// ============================================================================
// ML RISK SCORE
// ============================================================================
export function getDemoRiskScore(alienId) {
  const p = _profile(alienId);
  const cfg = {
    low:    { score: 1.72, category: 'low',      trend: 'stable',     change: 0,    prev: 1.76, conf: 0.91 },
    medium: { score: 2.80, category: 'medium',   trend: 'stable',     change: 0.08, prev: 2.72, conf: 0.87 },
    high:   { score: 4.12, category: 'critical',  trend: 'increasing', change: 0.32, prev: 3.80, conf: 0.84 },
  }[p];

  const factors = {
    low: {
      anomalyFrequency:  { score: 0.12,  weight: 0.30, raw: 0.10 },
      complianceHistory: { score: 0.12,  weight: 0.30, raw: 0.10 },
      patternStability:  { score: 0.20,  weight: 0.25, raw: 0.20 },
      recencyFactor:     { score: 0.28,  weight: 0.15, raw: 0.47 },
      associationRisk:   { score: 0,     weight: 0.00, raw: 0 },
    },
    medium: {
      anomalyFrequency:  { score: 0.48,  weight: 0.30, raw: 0.40 },
      complianceHistory: { score: 0.36,  weight: 0.30, raw: 0.30 },
      patternStability:  { score: 0.60,  weight: 0.25, raw: 0.60 },
      recencyFactor:     { score: 0.36,  weight: 0.15, raw: 0.60 },
      associationRisk:   { score: 0,     weight: 0.00, raw: 0 },
    },
    high: {
      anomalyFrequency:  { score: 0.96,  weight: 0.30, raw: 0.80 },
      complianceHistory: { score: 0.84,  weight: 0.30, raw: 0.70 },
      patternStability:  { score: 0.80,  weight: 0.25, raw: 0.80 },
      recencyFactor:     { score: 0.54,  weight: 0.15, raw: 0.90 },
      associationRisk:   { score: 0,     weight: 0.00, raw: 0 },
    },
  }[p];

  return {
    alienId: Number(alienId),
    score: cfg.score,
    riskScore: cfg.score,
    risk_score: cfg.score,
    category: cfg.category,
    modelConfidence: cfg.conf,
    calculatedAt: now(),
    trendDirection: cfg.trend,
    trendChange: cfg.change,
    factors,
    previousScore: cfg.prev,
  };
}

export function getDemoRiskScoreHistory(alienId, days = 30) {
  const p = _profile(alienId);
  const base = { low: 1.64, medium: 2.68, high: 3.72 }[p];
  const amplitude = { low: 0.12, medium: 0.24, high: 0.40 }[p];
  const drift = { low: 0.0, medium: 0.004, high: 0.012 }[p];

  const scores = [];
  for (let i = days; i >= 0; i--) {
    const val = base + Math.sin(i * 0.3) * amplitude + (days - i) * drift;
    scores.push({
      alienId: Number(alienId),
      score: Math.round(Math.min(Math.max(val, 1.0), 5.0) * 100) / 100,
      calculatedAt: daysAgo(i),
    });
  }
  return { alienId: Number(alienId), scores, count: scores.length };
}

export function getDemoRiskFactorExplanation(alienId) {
  const p = _profile(alienId);
  const riskScore = getDemoRiskScore(alienId);

  const explanations = {
    low: {
      anomaly_frequency: { contribution: 0.12, value: 0.10, weight: 0.30, explanation: 'No anomalies detected in the past 30 days. All behavior within normal parameters.', trend: 'stable' },
      compliance_violations: { contribution: 0.12, value: 0.10, weight: 0.30, explanation: 'Fully compliant. No missed check-ins, no geofence or curfew violations.', trend: 'stable' },
      pattern_stability: { contribution: 0.20, value: 0.20, weight: 0.25, explanation: 'Highly consistent daily routine with minimal variation.', trend: 'stable' },
      recency_factor: { contribution: 0.28, value: 0.47, weight: 0.15, explanation: 'No recent risk indicators in the last 72 hours.', trend: 'stable' },
      association_risk: { contribution: 0, value: 0, weight: 0.00, explanation: 'Association risk factor is not yet implemented.', trend: 'stable' },
    },
    medium: {
      anomaly_frequency: { contribution: 0.48, value: 0.40, weight: 0.30, explanation: 'Subject has 3 anomalies detected in the past 30 days, with 1 medium-severity event.', trend: 'stable' },
      compliance_violations: { contribution: 0.36, value: 0.30, weight: 0.30, explanation: 'Subject has been mostly compliant with 2 minor boundary violations this month.', trend: 'improving' },
      pattern_stability: { contribution: 0.60, value: 0.60, weight: 0.25, explanation: 'Subject routine has been moderately disrupted with unexpected evening travel.', trend: 'declining' },
      recency_factor: { contribution: 0.36, value: 0.60, weight: 0.15, explanation: 'Moderate risk indicators in the last 72 hours.', trend: 'stable' },
      association_risk: { contribution: 0, value: 0, weight: 0.00, explanation: 'Association risk factor is not yet implemented.', trend: 'stable' },
    },
    high: {
      anomaly_frequency: { contribution: 0.96, value: 0.80, weight: 0.30, explanation: 'Subject has 9 anomalies detected in the past 30 days, with 4 high-severity events including unusual night travel.', trend: 'increasing' },
      compliance_violations: { contribution: 0.84, value: 0.70, weight: 0.30, explanation: 'Subject has 5 missed check-ins and 3 geofence violations this month.', trend: 'worsening' },
      pattern_stability: { contribution: 0.80, value: 0.80, weight: 0.25, explanation: 'Significant routine breakdown. New locations appearing, home dwell time declining sharply.', trend: 'declining' },
      recency_factor: { contribution: 0.54, value: 0.90, weight: 0.15, explanation: 'High-severity alert within last 24 hours. Repeated visits to airport area.', trend: 'increasing' },
      association_risk: { contribution: 0, value: 0, weight: 0.00, explanation: 'Association risk factor is not yet implemented.', trend: 'stable' },
    },
  }[p];

  const topFactors = {
    low: ['recency_factor', 'pattern_stability', 'anomaly_frequency'],
    medium: ['pattern_stability', 'anomaly_frequency', 'recency_factor'],
    high: ['anomaly_frequency', 'compliance_violations', 'pattern_stability'],
  }[p];

  const recommendations = {
    low: ['Continue current monitoring level', 'Eligible for reduced check-in frequency'],
    medium: ['Review recent anomaly events for escalation patterns', 'Verify compliance with check-in schedule', 'Monitor evening travel patterns for deviations'],
    high: ['Immediately review repeated airport area visits', 'Escalate to supervisor — flight risk indicators elevated', 'Consider increasing check-in frequency', 'Investigate association with high-risk contact David Chen'],
  }[p];

  return {
    alienId: Number(alienId),
    score: riskScore.score,
    category: riskScore.category,
    factorExplanations: explanations,
    topFactors,
    recommendations,
    calculatedAt: now(),
  };
}

// ============================================================================
// MOVEMENT ANALYTICS
// ============================================================================
export function getDemoMovementSummary(personId, hours = 24) {
  const p = _profile(personId);

  const cfg = {
    low: {
      speed: 0.0, heading: null, stationary: true, stationaryMins: 310,
      state: 'stationary', distance: 8.2, avgSpeed: 18.5, maxSpeed: 42.1,
      points: 52, headingDir: 'N',
      lastLoc: { latitude: DEMO_LOCATIONS.home.lat, longitude: DEMO_LOCATIONS.home.lon, timestamp: hoursAgo(0.3) },
    },
    medium: {
      speed: 0.0, heading: null, stationary: true, stationaryMins: 127,
      state: 'stationary', distance: 14.3, avgSpeed: 22.5, maxSpeed: 58.2,
      points: 47, headingDir: 'N',
      lastLoc: { latitude: DEMO_LOCATIONS.home.lat, longitude: DEMO_LOCATIONS.home.lon, timestamp: hoursAgo(0.5) },
    },
    high: {
      speed: 62.4, heading: 225, stationary: false, stationaryMins: 0,
      state: 'driving', distance: 47.8, avgSpeed: 38.6, maxSpeed: 92.3,
      points: 83, headingDir: 'SW',
      lastLoc: { latitude: 27.9756, longitude: -82.5333, timestamp: hoursAgo(0.1) },
    },
  }[p];

  return {
    person_id: Number(personId),
    analysis_timestamp: now(),
    current_speed_mph: cfg.speed,
    current_heading_degrees: cfg.heading,
    is_stationary: cfg.stationary,
    stationary_duration_mins: cfg.stationaryMins,
    movement_state: cfg.state,
    distance_traveled_today_miles: cfg.distance,
    avg_speed_today_mph: cfg.avgSpeed,
    max_speed_today_mph: cfg.maxSpeed,
    points_analyzed: cfg.points,
    last_known_location: cfg.lastLoc,
    heading_direction: cfg.headingDir,
  };
}

export function getDemoVelocityThreshold(personId, thresholdMph = 85) {
  const p = _profile(personId);

  if (p === 'high') {
    return {
      person_id: Number(personId),
      alert: true,
      is_alert: true,
      alert_type: 'suspicious_speed',
      speed_mph: 92.3,
      threshold_mph: thresholdMph,
      severity: 'warning',
      details: `Speed of 92.3 mph exceeded threshold of ${thresholdMph} mph. Subject was heading SW on I-275 toward Tampa International Airport.`,
      latitude: 27.9650,
      longitude: -82.5100,
      timestamp: hoursAgo(1.5),
      message: 'Velocity threshold exceeded.',
    };
  }

  return {
    person_id: Number(personId),
    alert: false,
    is_alert: false,
    current_speed_mph: 0.0,
    threshold_mph: thresholdMph,
    message: 'Speed is within acceptable limits.',
    timestamp: now(),
  };
}

// ============================================================================
// GPS PIPELINE
// ============================================================================
export function getDemoGPSQualityStats(personId, days = 30) {
  const p = _profile(personId);
  const cfg = {
    low:    { total: 52, valid: 52, rejected: 0, quality: 94, avgAcc: 5.2, sats: 13.8, spoof: 0.01, battery: 0, spoofFlags: 0 },
    medium: { total: 47, valid: 46, rejected: 1, quality: 85, avgAcc: 8.3, sats: 11.2, spoof: 0.05, battery: 2, spoofFlags: 1 },
    high:   { total: 83, valid: 71, rejected: 12, quality: 68, avgAcc: 18.7, sats: 7.4, spoof: 0.22, battery: 8, spoofFlags: 6 },
  }[p];

  return {
    personId: Number(personId),
    totalPoints: cfg.total,
    total_points: cfg.total,
    validPoints: cfg.valid,
    rejectedPoints: cfg.rejected,
    avgQualityScore: cfg.quality / 100,
    averageQuality: cfg.quality,
    average_quality: cfg.quality,
    rejectionRate: cfg.rejected / cfg.total,
    rejection_rate: cfg.rejected / cfg.total,
    avgAccuracyMeters: cfg.avgAcc,
    avgSatelliteCount: cfg.sats,
    avgSpoofRisk: cfg.spoof,
    lowBatteryCount: cfg.battery,
    spoofFlagCount: cfg.spoofFlags,
    periodStart: daysAgo(days),
    periodEnd: now(),
    calculatedAt: now(),
  };
}

export function getDemoCheckSpoofing(personId) {
  const p = _profile(personId);

  if (p === 'low') {
    return {
      personId: Number(personId),
      spoofProbability: 0.03,
      pointsAnalyzed: 52,
      recommendation: 'accept',
      indicators: [],
      summary: 'GPS data is highly authentic. No spoofing indicators detected.',
      timestamp: now(),
    };
  }

  if (p === 'high') {
    return {
      personId: Number(personId),
      spoofProbability: 0.72,
      pointsAnalyzed: 83,
      recommendation: 'reject',
      indicators: [
        { indicatorType: 'suspicious_velocity', severity: 'high', value: 312.5, details: 'Speed of 312 mph between two consecutive points — physically impossible for ground travel.' },
        { indicatorType: 'teleportation', severity: 'high', value: 142.3, details: 'Subject appeared 142 miles away within 8 minutes.' },
        { indicatorType: 'no_satellites', severity: 'medium', value: 0, details: '0 satellites reported for 6 consecutive points, suggesting mock location.' },
        { indicatorType: 'perfect_accuracy', severity: 'medium', value: 0.0, details: 'Exact 0.0m accuracy reported — synthetic GPS signature.' },
      ],
      summary: 'HIGH PROBABILITY OF GPS SPOOFING. Multiple critical indicators including impossible velocity and teleportation patterns. Recommend immediate manual verification.',
      timestamp: now(),
    };
  }

  // medium
  return {
    personId: Number(personId),
    spoofProbability: 0.12,
    pointsAnalyzed: 47,
    recommendation: 'accept',
    indicators: [
      { indicatorType: 'constant_coordinates', severity: 'low', value: 3, details: '3 consecutive identical coordinates detected (likely stationary)' },
    ],
    summary: 'GPS data appears authentic. Minor constant-coordinate pattern consistent with stationary behavior.',
    timestamp: now(),
  };
}

export function getDemoSpoofHistory(personId, days = 7) {
  const p = _profile(personId);

  if (p === 'low') {
    return {
      personId: Number(personId),
      analysisCount: 3,
      analyses: [
        { spoofProbability: 0.02, pointsAnalyzed: 18, recommendation: 'accept', indicatorCount: 0, indicators: [], timestamp: daysAgo(1) },
        { spoofProbability: 0.01, pointsAnalyzed: 20, recommendation: 'accept', indicatorCount: 0, indicators: [], timestamp: daysAgo(3) },
        { spoofProbability: 0.03, pointsAnalyzed: 14, recommendation: 'accept', indicatorCount: 0, indicators: [], timestamp: daysAgo(6) },
      ],
    };
  }

  if (p === 'high') {
    return {
      personId: Number(personId),
      analysisCount: 5,
      analyses: [
        {
          spoofProbability: 0.72, pointsAnalyzed: 25, recommendation: 'reject', indicatorCount: 4,
          indicators: [
            { indicatorType: 'suspicious_velocity', severity: 'high', value: 312.5 },
            { indicatorType: 'teleportation', severity: 'high', value: 142.3 },
            { indicatorType: 'no_satellites', severity: 'medium', value: 0 },
            { indicatorType: 'perfect_accuracy', severity: 'medium', value: 0.0 },
          ],
          timestamp: daysAgo(0),
        },
        {
          spoofProbability: 0.58, pointsAnalyzed: 18, recommendation: 'flag', indicatorCount: 2,
          indicators: [
            { indicatorType: 'suspicious_velocity', severity: 'high', value: 215.3 },
            { indicatorType: 'no_satellites', severity: 'medium', value: 0 },
          ],
          timestamp: daysAgo(2),
        },
        {
          spoofProbability: 0.65, pointsAnalyzed: 22, recommendation: 'flag', indicatorCount: 3,
          indicators: [
            { indicatorType: 'teleportation', severity: 'high', value: 98.7 },
            { indicatorType: 'constant_coordinates', severity: 'low', value: 8 },
            { indicatorType: 'no_satellites', severity: 'medium', value: 0 },
          ],
          timestamp: daysAgo(4),
        },
        { spoofProbability: 0.35, pointsAnalyzed: 15, recommendation: 'flag', indicatorCount: 1,
          indicators: [{ indicatorType: 'suspicious_velocity', severity: 'high', value: 188.0 }], timestamp: daysAgo(5) },
        { spoofProbability: 0.15, pointsAnalyzed: 20, recommendation: 'accept', indicatorCount: 0, indicators: [], timestamp: daysAgo(6) },
      ],
    };
  }

  // medium
  return {
    personId: Number(personId),
    analysisCount: 3,
    analyses: [
      { spoofProbability: 0.08, pointsAnalyzed: 15, recommendation: 'accept', indicatorCount: 0, indicators: [], timestamp: daysAgo(1) },
      {
        spoofProbability: 0.45, pointsAnalyzed: 12, recommendation: 'flag', indicatorCount: 2,
        indicators: [
          { indicatorType: 'suspicious_velocity', severity: 'high', value: 215.3, details: 'Speed of 215 mph between two points' },
          { indicatorType: 'no_satellites', severity: 'medium', value: 0, details: '0 satellites reported for 3 points' },
        ],
        timestamp: daysAgo(4),
      },
      { spoofProbability: 0.05, pointsAnalyzed: 20, recommendation: 'accept', indicatorCount: 0, indicators: [], timestamp: daysAgo(6) },
    ],
  };
}

// ============================================================================
// PATTERN OF LIFE
// ============================================================================
export function getDemoPOLModel(personId) {
  const p = _profile(personId);
  const locations = Object.entries(DEMO_LOCATIONS).map(([key, loc], idx) => ({
    locationId: `loc_${String(idx).padStart(3, '0')}`,
    locationType: loc.type,
    label: loc.name,
    centerLat: loc.lat,
    centerLon: loc.lon,
    radiusMeters: loc.type === 'home' ? 120 : loc.type === 'work' ? 150 : 130,
    visitCount: loc.type === 'home' ? 56 : loc.type === 'work' ? 40 : 8 + idx * 3,
    avgDwellMinutes: loc.type === 'home' ? 600 : loc.type === 'work' ? 480 : 45 + idx * 10,
    confidenceScore: loc.type === 'home' ? 1.0 : loc.type === 'work' ? 0.95 : 0.6 + idx * 0.05,
    firstSeen: daysAgo(60),
    lastSeen: daysAgo(idx),
  }));

  // Build routine matrix
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const routineMatrix = {};
  locations.forEach((loc) => {
    routineMatrix[loc.locationId] = {};
    dayNames.forEach((dayName, dayIdx) => {
      routineMatrix[loc.locationId][dayName] = {};
      const periods = ['morning', 'midday', 'afternoon', 'evening', 'night'];
      const isWeekend = dayIdx >= 5;
      periods.forEach((period) => {
        let prob = 0;
        if (loc.locationType === 'home') {
          prob = (period === 'night' || period === 'morning') ? 0.85 : (isWeekend ? 0.5 : 0.15);
        } else if (loc.locationType === 'work') {
          prob = (!isWeekend && (period === 'midday' || period === 'afternoon')) ? 0.8 : 0.05;
        } else {
          prob = period === 'evening' ? 0.2 : 0.05;
        }
        routineMatrix[loc.locationId][dayName][period] = Math.round(prob * 100) / 100;
      });
    });
  });

  const patternDrift = {
    low: {
      stabilityScore: 96,
      homeDriftMeters: 3,
      workDriftMeters: 8,
      routineShiftPct: 1,
      locationsOldCount: locations.length,
      locationsNewCount: locations.length,
      previousModelTimestamp: daysAgo(8),
      locationChanges: [],
    },
    medium: {
      stabilityScore: 72,
      homeDriftMeters: 12,
      workDriftMeters: 45,
      routineShiftPct: 8,
      locationsOldCount: locations.length,
      locationsNewCount: locations.length,
      previousModelTimestamp: daysAgo(8),
      locationChanges: [
        { type: 'work', change: 'moved', distanceMeters: 45 },
      ],
    },
    high: {
      stabilityScore: 35,
      homeDriftMeters: 280,
      workDriftMeters: 520,
      routineShiftPct: 32,
      locationsOldCount: 4,
      locationsNewCount: locations.length,
      previousModelTimestamp: daysAgo(8),
      locationChanges: [
        { type: 'home', change: 'moved', distanceMeters: 280 },
        { type: 'work', change: 'moved', distanceMeters: 520 },
        { type: 'frequent', change: 'added', oldCount: 2, newCount: 4 },
      ],
    },
  }[p];

  return {
    personId: Number(personId),
    recordCount: { low: 224, medium: 187, high: 312 }[p],
    modelTimestamp: daysAgo(1),
    significantLocations: locations,
    routineMatrix,
    patternDrift,
    summary: {
      totalLocations: locations.length,
      homeLocation: locations[0],
      workLocation: locations[1],
      frequentLocations: locations.slice(2),
      modelAge: '1 day',
    },
  };
}

export function getDemoDeviation(personId, lat, lon) {
  const p = _profile(personId);
  const useLat = lat || DEMO_LOCATIONS.home.lat;
  const useLon = lon || DEMO_LOCATIONS.home.lon;
  const isNearHome = Math.abs(useLat - DEMO_LOCATIONS.home.lat) < 0.005 && Math.abs(useLon - DEMO_LOCATIONS.home.lon) < 0.005;

  if (p === 'low') {
    return {
      personId: Number(personId),
      deviationScore: 0.04,
      alertLevel: 'none',
      isDeviation: false,
      expectedLocation: 'Home',
      currentLocationType: 'home',
      locationChecked: { latitude: DEMO_LOCATIONS.home.lat, longitude: DEMO_LOCATIONS.home.lon, usedLatestCheckin: !lat },
      confidence: 0.97,
      timestamp: now(),
    };
  }

  if (p === 'high') {
    return {
      personId: Number(personId),
      deviationScore: 0.85,
      alertLevel: 'high',
      isDeviation: true,
      expectedLocation: 'Home',
      currentLocationType: 'unknown',
      locationChecked: { latitude: 27.9756, longitude: -82.5333, usedLatestCheckin: !lat },
      confidence: 0.92,
      timestamp: now(),
    };
  }

  // medium
  return {
    personId: Number(personId),
    deviationScore: isNearHome ? 0.08 : 0.72,
    alertLevel: isNearHome ? 'none' : 'medium',
    isDeviation: !isNearHome,
    expectedLocation: 'Home',
    currentLocationType: isNearHome ? 'home' : 'unknown',
    locationChecked: { latitude: useLat, longitude: useLon, usedLatestCheckin: !lat },
    confidence: 0.91,
    timestamp: now(),
  };
}

export function getDemoAnomalyHistory(alienId, limit = 10) {
  const p = _profile(alienId);
  const types = ['temporal', 'spatial', 'route', 'behavioral'];

  if (p === 'low') {
    const detections = [];
    for (let i = 0; i < Math.min(limit, 6); i++) {
      detections.push({
        id: `anom_${i}`, personId: Number(alienId), isAnomaly: false,
        anomalyScore: 0.05 + Math.random() * 0.10, alertLevel: 'none', anomalyType: null,
        confidence: 0.85 + Math.random() * 0.12, expectedLocation: 'Home',
        createdAt: hoursAgo(i * 12 + Math.random() * 4),
      });
    }
    return { alienId: Number(alienId), count: detections.length, anomalies: detections, detections };
  }

  if (p === 'high') {
    const levels = ['high', 'high', 'medium', 'high', 'medium', 'medium', 'high', 'low', 'medium', 'high'];
    const detections = [];
    for (let i = 0; i < Math.min(limit, 10); i++) {
      const isAnomaly = levels[i] !== 'none';
      detections.push({
        id: `anom_${i}`, personId: Number(alienId), isAnomaly,
        anomalyScore: 0.6 + Math.random() * 0.35, alertLevel: levels[i],
        anomalyType: types[i % types.length],
        confidence: 0.7 + Math.random() * 0.25, expectedLocation: 'Home',
        createdAt: hoursAgo(i * 6 + Math.random() * 3),
      });
    }
    return { alienId: Number(alienId), count: detections.length, anomalies: detections, detections };
  }

  // medium
  const levels = ['none', 'low', 'low', 'medium', 'none', 'none', 'low', 'medium', 'none', 'low'];
  const detections = [];
  for (let i = 0; i < Math.min(limit, 10); i++) {
    const isAnomaly = levels[i] !== 'none';
    detections.push({
      id: `anom_${i}`, personId: Number(alienId), isAnomaly,
      anomalyScore: isAnomaly ? 0.35 + Math.random() * 0.30 : 0.08 + Math.random() * 0.15,
      alertLevel: levels[i], anomalyType: isAnomaly ? types[i % types.length] : null,
      confidence: 0.7 + Math.random() * 0.25, expectedLocation: 'Home',
      createdAt: hoursAgo(i * 8 + Math.random() * 4),
    });
  }
  return { alienId: Number(alienId), count: detections.length, anomalies: detections, detections };
}

export function getDemoDetectAnomaly(personId, lat, lon) {
  const p = _profile(personId);

  if (p === 'low') {
    return {
      personId: Number(personId), isAnomaly: false, anomalyScore: 0.06, alertLevel: 'none', anomalyType: null,
      confidence: 0.95, expectedLocation: 'Home',
      locationChecked: { latitude: lat || DEMO_LOCATIONS.home.lat, longitude: lon || DEMO_LOCATIONS.home.lon },
      ensembleResults: { isolationForest: { score: 0.04, isAnomaly: false }, autoEncoder: { score: 0.07, isAnomaly: false }, statistical: { score: 0.05, isAnomaly: false } },
      timestamp: now(),
    };
  }

  if (p === 'high') {
    return {
      personId: Number(personId), isAnomaly: true, anomalyScore: 0.82, alertLevel: 'high', anomalyType: 'spatial',
      confidence: 0.89, expectedLocation: 'Home',
      locationChecked: { latitude: lat || 27.9756, longitude: lon || -82.5333 },
      ensembleResults: { isolationForest: { score: 0.78, isAnomaly: true }, autoEncoder: { score: 0.85, isAnomaly: true }, statistical: { score: 0.82, isAnomaly: true } },
      timestamp: now(),
    };
  }

  // medium
  return {
    personId: Number(personId), isAnomaly: false, anomalyScore: 0.15, alertLevel: 'none', anomalyType: null,
    confidence: 0.88, expectedLocation: 'Home',
    locationChecked: { latitude: lat || DEMO_LOCATIONS.home.lat, longitude: lon || DEMO_LOCATIONS.home.lon },
    ensembleResults: { isolationForest: { score: 0.12, isAnomaly: false }, autoEncoder: { score: 0.18, isAnomaly: false }, statistical: { score: 0.14, isAnomaly: false } },
    timestamp: now(),
  };
}

export function getDemoTrainEnsemble(personId) {
  return {
    personId: Number(personId),
    status: 'trained',
    samplesUsed: { low: 224, medium: 187, high: 312 }[_profile(personId)],
    models: ['IsolationForest', 'AutoEncoder', 'Statistical'],
    trainedAt: now(),
  };
}

export function getDemoBuildPOL(personId) {
  return getDemoPOLModel(personId);
}

// ============================================================================
// SMART GEOFENCING
// ============================================================================
export function getDemoSmartGeofences(personId) {
  const p = _profile(personId);
  const geofences = Object.entries(DEMO_LOCATIONS).map(([key, loc], idx) => ({
    geofence_id: `SGF-${personId}-loc_${String(idx).padStart(3, '0')}`,
    person_id: Number(personId),
    fence_type: loc.type,
    name: loc.type === 'home' ? 'Home Location' : loc.type === 'work' ? 'Work Location' : `Frequent Location - ${loc.name}`,
    center_lat: loc.lat,
    center_lon: loc.lon,
    radius_meters: loc.type === 'home' ? 120 : loc.type === 'work' ? 150 : 130,
    confidence: loc.type === 'home' ? 1.0 : loc.type === 'work' ? 0.95 : 0.6 + idx * 0.05,
    alert_on: loc.type === 'home' ? 'exit' : 'none',
    active_hours: loc.type === 'home'
      ? { start: '18:00', end: '08:00', days: [0, 1, 2, 3, 4, 5, 6] }
      : loc.type === 'work'
      ? { start: '08:00', end: '18:00', days: [0, 1, 2, 3, 4] }
      : null,
    is_active: true,
    source_location_id: `loc_${String(idx).padStart(3, '0')}`,
    created_at: daysAgo(30),
    updated_at: daysAgo(1),
  }));

  const homeMismatch = {
    low:    {},
    medium: {},
    high:   { mismatch: true, distanceMeters: 1850, registeredLat: 27.9350, registeredLon: -82.4300, detectedLat: DEMO_LOCATIONS.home.lat, detectedLon: DEMO_LOCATIONS.home.lon },
  }[p];

  return {
    personId: Number(personId),
    geofenceCount: geofences.length,
    geofences,
    homeMismatch,
    polModelTimestamp: daysAgo(1),
  };
}

export function getDemoGeofenceCompliance(personId, lat, lon) {
  const p = _profile(personId);

  if (p === 'high') {
    return {
      personId: Number(personId),
      is_compliant: false, compliance_status: 'outside_all', isCompliant: false,
      status: 'Outside all geofences',
      expected_geofence: 'Home Location', actual_geofence: 'None',
      distance_from_expected_meters: 4820,
      details: 'Subject is 4.8 km from home geofence. Currently near Tampa International Airport — flagged as flight risk location.',
      checked_location: { latitude: lat || 27.9756, longitude: lon || -82.5333 },
      timestamp: now(),
    };
  }

  return {
    personId: Number(personId),
    is_compliant: true, compliance_status: 'at_home', isCompliant: true,
    status: 'Within home geofence',
    expected_geofence: 'Home Location', actual_geofence: 'Home Location',
    distance_from_expected_meters: p === 'low' ? 8.2 : 15.3,
    details: `Subject is within the Home Location geofence (${p === 'low' ? '8' : '15'}m from center).`,
    checked_location: { latitude: lat || DEMO_LOCATIONS.home.lat, longitude: lon || DEMO_LOCATIONS.home.lon },
    timestamp: now(),
  };
}

// ============================================================================
// PATTERN CLUSTERING
// ============================================================================
export function getDemoClusterAssignment(alienId) {
  const p = _profile(alienId);
  const cfg = {
    low:    { clusterId: 0, label: 'routine_stable',       dist: 0.11, sil: 0.88 },
    medium: { clusterId: 1, label: 'mobile_regular',       dist: 0.23, sil: 0.72 },
    high:   { clusterId: 4, label: 'high_risk_exposure',   dist: 0.45, sil: 0.54 },
  }[p];

  return {
    alienId: Number(alienId), personId: Number(alienId),
    clusterId: cfg.clusterId, clusterLabel: cfg.label, cluster_label: cfg.label,
    distanceToCentroid: cfg.dist, silhouetteScore: cfg.sil,
    assignedAt: daysAgo(1),
    features: getDemoBehavioralFeatures(alienId),
  };
}

// ============================================================================
// ROUTE RECONSTRUCTION
// ============================================================================
export function getDemoRouteHistory(personId, hours = 24) {
  const p = _profile(personId);
  const baseTime = Date.now() - hours * 3600000;

  const routeConfigs = {
    low: {
      waypoints: [
        { lat: DEMO_LOCATIONS.home.lat, lon: DEMO_LOCATIONS.home.lon, speed: 0, stop: true, stopMins: 540, label: 'Home' },
        { lat: 27.9510, lon: -82.4570, speed: 22, stop: false },
        { lat: DEMO_LOCATIONS.work.lat, lon: DEMO_LOCATIONS.work.lon, speed: 0, stop: true, stopMins: 480, label: 'Work' },
        { lat: 27.9450, lon: -82.4520, speed: 20, stop: false },
        { lat: DEMO_LOCATIONS.grocery.lat, lon: DEMO_LOCATIONS.grocery.lon, speed: 0, stop: true, stopMins: 25, label: 'Grocery' },
        { lat: 27.9490, lon: -82.4560, speed: 18, stop: false },
        { lat: DEMO_LOCATIONS.home.lat, lon: DEMO_LOCATIONS.home.lon, speed: 0, stop: true, stopMins: 310, label: 'Home' },
      ],
      totalDist: 8.2, avgSpeed: 18.5, maxSpeed: 28.0,
    },
    medium: {
      waypoints: [
        { lat: DEMO_LOCATIONS.home.lat, lon: DEMO_LOCATIONS.home.lon, speed: 0, stop: true, stopMins: 480, label: 'Home' },
        { lat: 27.9510, lon: -82.4570, speed: 25, stop: false },
        { lat: 27.9515, lon: -82.4575, speed: 32, stop: false },
        { lat: 27.9480, lon: -82.4580, speed: 28, stop: false },
        { lat: DEMO_LOCATIONS.work.lat, lon: DEMO_LOCATIONS.work.lon, speed: 0, stop: true, stopMins: 240, label: 'Work' },
        { lat: 27.9485, lon: -82.4580, speed: 22, stop: false },
        { lat: 27.9550, lon: -82.4600, speed: 35, stop: false },
        { lat: 27.9600, lon: -82.4620, speed: 30, stop: false },
        { lat: DEMO_LOCATIONS.gym.lat, lon: DEMO_LOCATIONS.gym.lon, speed: 0, stop: true, stopMins: 60, label: 'Gym' },
        { lat: 27.9580, lon: -82.4610, speed: 28, stop: false },
        { lat: 27.9540, lon: -82.4590, speed: 25, stop: false },
        { lat: DEMO_LOCATIONS.home.lat, lon: DEMO_LOCATIONS.home.lon, speed: 0, stop: true, stopMins: 127, label: 'Home' },
      ],
      totalDist: 14.3, avgSpeed: 22.5, maxSpeed: 35.0,
    },
    high: {
      waypoints: [
        { lat: DEMO_LOCATIONS.home.lat, lon: DEMO_LOCATIONS.home.lon, speed: 0, stop: true, stopMins: 180, label: 'Home' },
        { lat: 27.9550, lon: -82.4600, speed: 45, stop: false },
        { lat: 27.9600, lon: -82.4700, speed: 62, stop: false },
        { lat: 27.9617, lon: -82.4380, speed: 58, stop: false },
        { lat: 27.9617, lon: -82.4380, speed: 0, stop: true, stopMins: 45, label: 'Ybor Nightclub' },
        { lat: 27.9550, lon: -82.4500, speed: 72, stop: false },
        { lat: 27.9650, lon: -82.5100, speed: 88, stop: false },
        { lat: 27.9700, lon: -82.5200, speed: 92, stop: false },
        { lat: 27.9756, lon: -82.5333, speed: 0, stop: true, stopMins: 35, label: 'Airport Area' },
        { lat: 27.9700, lon: -82.5100, speed: 75, stop: false },
        { lat: 27.9800, lon: -82.4100, speed: 68, stop: false },
        { lat: 27.9800, lon: -82.4100, speed: 0, stop: true, stopMins: 90, label: 'Associate Residence' },
        { lat: 27.9650, lon: -82.4400, speed: 55, stop: false },
        { lat: DEMO_LOCATIONS.home.lat, lon: DEMO_LOCATIONS.home.lon, speed: 0, stop: true, stopMins: 60, label: 'Home' },
      ],
      totalDist: 47.8, avgSpeed: 38.6, maxSpeed: 92.3,
    },
  };

  const cfg = routeConfigs[p];
  const pts = cfg.waypoints;

  const routePoints = pts.map((wp, idx) => ({
    latitude: wp.lat,
    longitude: wp.lon,
    timestamp: new Date(baseTime + idx * (hours * 3600000 / pts.length)).toISOString(),
    speed_mph: wp.speed,
    heading_degrees: idx > 0 ? Math.random() * 360 : 0,
    is_stop: wp.stop || false,
    stop_duration_mins: wp.stopMins || 0,
  }));

  const stops = pts
    .filter((wp) => wp.stop)
    .map((wp, idx) => ({
      stop_id: `stop_${idx}`,
      latitude: wp.lat,
      longitude: wp.lon,
      arrival_time: routePoints.find((rp) => rp.latitude === wp.lat && rp.longitude === wp.lon)?.timestamp,
      departure_time: hoursAgo(2),
      duration_mins: wp.stopMins,
      point_count: 3 + idx,
      label: wp.label,
    }));

  return {
    person_id: Number(personId),
    point_count: routePoints.length,
    total_distance_miles: cfg.totalDist,
    total_duration_mins: hours * 60,
    moving_duration_mins: p === 'low' ? 38 : p === 'medium' ? 62 : 145,
    stopped_duration_mins: hours * 60 - (p === 'low' ? 38 : p === 'medium' ? 62 : 145),
    avg_speed_mph: cfg.avgSpeed,
    max_speed_mph: cfg.maxSpeed,
    start_time: routePoints[0].timestamp,
    end_time: routePoints[routePoints.length - 1].timestamp,
    points: routePoints,
    stops,
    stop_count: stops.length,
  };
}

// ============================================================================
// HIGH-RISK ZONES
// ============================================================================
export function getDemoZoneVisitStats(alienId, days = 30) {
  const p = _profile(alienId);

  if (p === 'low') {
    return {
      alienId: Number(alienId), totalVisits: 0, uniqueZones: 0,
      totalDwellSeconds: 0, totalDwellMinutes: 0, exposureScore: 0, zoneExposureScore: 0,
      maxRiskLevel: 0, highestRiskLevel: 0, lastVisitTime: null,
      visitsByCategory: {}, riskBreakdown: { 1: { visits: 0, dwellMinutes: 0 }, 2: { visits: 0, dwellMinutes: 0 }, 3: { visits: 0, dwellMinutes: 0 }, 4: { visits: 0, dwellMinutes: 0 }, 5: { visits: 0, dwellMinutes: 0 } },
      period: { start: daysAgo(days), end: now() },
    };
  }

  if (p === 'high') {
    return {
      alienId: Number(alienId), totalVisits: 11, uniqueZones: 3,
      totalDwellSeconds: 9900, totalDwellMinutes: 165, exposureScore: 78, zoneExposureScore: 0.78,
      maxRiskLevel: 4, highestRiskLevel: 4, lastVisitTime: daysAgo(1),
      visitsByCategory: { 'flight_risk_location': 4, 'previous_arrest_location': 3, 'gang_territory': 4 },
      riskBreakdown: { 1: { visits: 0, dwellMinutes: 0 }, 2: { visits: 3, dwellMinutes: 35 }, 3: { visits: 4, dwellMinutes: 65 }, 4: { visits: 4, dwellMinutes: 65 }, 5: { visits: 0, dwellMinutes: 0 } },
      period: { start: daysAgo(days), end: now() },
    };
  }

  // medium
  return {
    alienId: Number(alienId), totalVisits: 4, uniqueZones: 2,
    totalDwellSeconds: 2700, totalDwellMinutes: 45, exposureScore: 32, zoneExposureScore: 0.32,
    maxRiskLevel: 3, highestRiskLevel: 3, lastVisitTime: daysAgo(5),
    visitsByCategory: { 'flight_risk_location': 2, 'previous_arrest_location': 2 },
    riskBreakdown: { 1: { visits: 0, dwellMinutes: 0 }, 2: { visits: 2, dwellMinutes: 20 }, 3: { visits: 2, dwellMinutes: 25 }, 4: { visits: 0, dwellMinutes: 0 }, 5: { visits: 0, dwellMinutes: 0 } },
    period: { start: daysAgo(days), end: now() },
  };
}

export function getDemoZoneVisitHistory(alienId, days = 30, limit = 15) {
  const p = _profile(alienId);

  if (p === 'low') {
    return { alienId: Number(alienId), visits: [], totalCount: 0 };
  }

  if (p === 'high') {
    return {
      alienId: Number(alienId),
      visits: [
        { visitId: 'zv_001', zoneId: 'zone_flight_risk_01', zoneName: 'Tampa International Airport', zoneCategory: 'flight_risk_location', riskLevel: 3, entryTime: daysAgo(1), exitTime: hoursAgo(22), dwellSeconds: 2400, latitude: 27.9756, longitude: -82.5333 },
        { visitId: 'zv_002', zoneId: 'zone_gang_01', zoneName: 'East Tampa - Known Gang Territory', zoneCategory: 'gang_territory', riskLevel: 4, entryTime: daysAgo(2), exitTime: hoursAgo(46), dwellSeconds: 3600, latitude: 27.9350, longitude: -82.4100 },
        { visitId: 'zv_003', zoneId: 'zone_flight_risk_01', zoneName: 'Tampa International Airport', zoneCategory: 'flight_risk_location', riskLevel: 3, entryTime: daysAgo(5), exitTime: hoursAgo(118), dwellSeconds: 1800, latitude: 27.9756, longitude: -82.5333 },
        { visitId: 'zv_004', zoneId: 'zone_prev_arrest_01', zoneName: 'Ybor District - Previous Incident', zoneCategory: 'previous_arrest_location', riskLevel: 2, entryTime: daysAgo(3), exitTime: hoursAgo(70), dwellSeconds: 2700, latitude: 27.9617, longitude: -82.4380 },
        { visitId: 'zv_005', zoneId: 'zone_gang_01', zoneName: 'East Tampa - Known Gang Territory', zoneCategory: 'gang_territory', riskLevel: 4, entryTime: daysAgo(7), exitTime: hoursAgo(166), dwellSeconds: 1500, latitude: 27.9350, longitude: -82.4100 },
        { visitId: 'zv_006', zoneId: 'zone_flight_risk_01', zoneName: 'Tampa International Airport', zoneCategory: 'flight_risk_location', riskLevel: 3, entryTime: daysAgo(10), exitTime: hoursAgo(238), dwellSeconds: 1200, latitude: 27.9756, longitude: -82.5333 },
      ],
      totalCount: 11,
    };
  }

  // medium
  return {
    alienId: Number(alienId),
    visits: [
      { visitId: 'zv_001', zoneId: 'zone_flight_risk_01', zoneName: 'Tampa International Airport', zoneCategory: 'flight_risk_location', riskLevel: 3, entryTime: daysAgo(5), exitTime: hoursAgo(5 * 24 - 2), dwellSeconds: 1320, latitude: 27.9756, longitude: -82.5333 },
      { visitId: 'zv_002', zoneId: 'zone_flight_risk_01', zoneName: 'Tampa International Airport', zoneCategory: 'flight_risk_location', riskLevel: 3, entryTime: daysAgo(12), exitTime: hoursAgo(12 * 24 - 1), dwellSeconds: 1380, latitude: 27.9756, longitude: -82.5333 },
      { visitId: 'zv_003', zoneId: 'zone_prev_arrest_01', zoneName: 'Ybor District - Previous Incident', zoneCategory: 'previous_arrest_location', riskLevel: 2, entryTime: daysAgo(8), exitTime: hoursAgo(8 * 24 - 0.5), dwellSeconds: 720, latitude: 27.9617, longitude: -82.4380 },
      { visitId: 'zv_004', zoneId: 'zone_prev_arrest_01', zoneName: 'Ybor District - Previous Incident', zoneCategory: 'previous_arrest_location', riskLevel: 2, entryTime: daysAgo(20), exitTime: hoursAgo(20 * 24 - 0.3), dwellSeconds: 480, latitude: 27.9617, longitude: -82.4380 },
    ],
    totalCount: 4,
  };
}

export function getDemoActiveZones() {
  return [
    {
      zoneId: 'zone_flight_risk_01', zoneName: 'Tampa International Airport', zoneCategory: 'flight_risk_location', riskLevel: 3, source: 'system',
      center: { latitude: 27.9756, longitude: -82.5333 },
      boundaryCoordinates: [[-82.5383, 27.9806], [-82.5283, 27.9806], [-82.5283, 27.9706], [-82.5383, 27.9706], [-82.5383, 27.9806]],
      isActive: true,
    },
    {
      zoneId: 'zone_prev_arrest_01', zoneName: 'Ybor District - Previous Incident', zoneCategory: 'previous_arrest_location', riskLevel: 2, source: 'case_history',
      center: { latitude: 27.9617, longitude: -82.4380 },
      boundaryCoordinates: [[-82.4410, 27.9647], [-82.4350, 27.9647], [-82.4350, 27.9587], [-82.4410, 27.9587], [-82.4410, 27.9647]],
      isActive: true,
    },
    {
      zoneId: 'zone_gang_01', zoneName: 'East Tampa - Known Gang Territory', zoneCategory: 'gang_territory', riskLevel: 4, source: 'intelligence',
      center: { latitude: 27.9350, longitude: -82.4100 },
      boundaryCoordinates: [[-82.4150, 27.9400], [-82.4050, 27.9400], [-82.4050, 27.9300], [-82.4150, 27.9300], [-82.4150, 27.9400]],
      isActive: true,
    },
  ];
}

export function getDemoZoneVisitMap(alienId, days = 30) {
  const p = _profile(alienId);

  const baseZones = [
    { zoneId: 'zone_flight_risk_01', name: 'Tampa International Airport', category: 'flight_risk_location', riskLevel: 3, latitude: 27.9756, longitude: -82.5333, radiusMeters: 500 },
    { zoneId: 'zone_prev_arrest_01', name: 'Ybor District - Previous Incident', category: 'previous_arrest_location', riskLevel: 2, latitude: 27.9617, longitude: -82.4380, radiusMeters: 300 },
  ];

  if (p === 'low') {
    return {
      alienId: Number(alienId),
      zones: baseZones.map(z => ({ ...z, visitCount: 0, totalDwellMinutes: 0 })),
      subjectTrail: [{ latitude: DEMO_LOCATIONS.home.lat, longitude: DEMO_LOCATIONS.home.lon, timestamp: daysAgo(1) }],
    };
  }

  if (p === 'high') {
    return {
      alienId: Number(alienId),
      zones: [
        { ...baseZones[0], visitCount: 4, totalDwellMinutes: 90 },
        { ...baseZones[1], visitCount: 3, totalDwellMinutes: 45 },
        { zoneId: 'zone_gang_01', name: 'East Tampa - Known Gang Territory', category: 'gang_territory', riskLevel: 4, latitude: 27.9350, longitude: -82.4100, radiusMeters: 400, visitCount: 4, totalDwellMinutes: 85 },
      ],
      subjectTrail: [
        { latitude: DEMO_LOCATIONS.home.lat, longitude: DEMO_LOCATIONS.home.lon, timestamp: daysAgo(1) },
        { latitude: 27.9617, longitude: -82.4380, timestamp: daysAgo(1) },
        { latitude: 27.9756, longitude: -82.5333, timestamp: daysAgo(1) },
        { latitude: 27.9350, longitude: -82.4100, timestamp: daysAgo(2) },
      ],
    };
  }

  // medium
  return {
    alienId: Number(alienId),
    zones: [
      { ...baseZones[0], visitCount: 2, totalDwellMinutes: 45 },
      { ...baseZones[1], visitCount: 2, totalDwellMinutes: 20 },
    ],
    subjectTrail: [
      { latitude: DEMO_LOCATIONS.home.lat, longitude: DEMO_LOCATIONS.home.lon, timestamp: daysAgo(1) },
      { latitude: DEMO_LOCATIONS.work.lat, longitude: DEMO_LOCATIONS.work.lon, timestamp: daysAgo(1) },
    ],
  };
}

// ============================================================================
// NETWORK INTELLIGENCE
// ============================================================================
export function getDemoNetworkAnalysis(alienId) {
  const pid = Number(alienId);
  const p = _profile(alienId);

  const cfg = {
    low:    { nodes: 3, edges: 2, communities: 1 },
    medium: { nodes: 4, edges: 3, communities: 2 },
    high:   { nodes: 6, edges: 7, communities: 3 },
  }[p];

  const connectedPersons = {
    low: [
      { personId: 1001, peopleId: 1001, PeopleId: 1001, firstName: 'Priya', lastName: 'Sharma', FirstName: 'Priya', LastName: 'Sharma', status: 'Active', Status: 'Active', depth: 1, coLocationCount: 5, interactionCount: 5, connectionWeight: 0.3, communityId: 0, riskScore: 12 },
      { personId: 1002, peopleId: 1002, PeopleId: 1002, firstName: 'David', lastName: 'Chen', FirstName: 'David', LastName: 'Chen', status: 'Active', Status: 'Active', depth: 2, coLocationCount: 2, interactionCount: 2, connectionWeight: 0.15, communityId: 0, riskScore: 8 },
    ],
    medium: [
      { personId: 1001, peopleId: 1001, PeopleId: 1001, firstName: 'Priya', lastName: 'Sharma', FirstName: 'Priya', LastName: 'Sharma', status: 'Active', Status: 'Active', depth: 1, coLocationCount: 12, interactionCount: 12, connectionWeight: 0.8, communityId: 0, riskScore: 28 },
      { personId: 1002, peopleId: 1002, PeopleId: 1002, firstName: 'David', lastName: 'Chen', FirstName: 'David', LastName: 'Chen', status: 'Active', Status: 'Active', depth: 1, coLocationCount: 8, interactionCount: 8, connectionWeight: 0.5, communityId: 1, riskScore: 45 },
      { personId: 1003, peopleId: 1003, PeopleId: 1003, firstName: 'Amara', lastName: 'Okafor', FirstName: 'Amara', LastName: 'Okafor', status: 'Compliant', Status: 'Compliant', depth: 2, coLocationCount: 5, interactionCount: 5, connectionWeight: 0.3, communityId: 0, riskScore: 15 },
    ],
    high: [
      { personId: 1001, peopleId: 1001, PeopleId: 1001, firstName: 'Priya', lastName: 'Sharma', FirstName: 'Priya', LastName: 'Sharma', status: 'Active', Status: 'Active', depth: 1, coLocationCount: 18, interactionCount: 18, connectionWeight: 0.6, communityId: 0, riskScore: 52 },
      { personId: 1003, peopleId: 1003, PeopleId: 1003, firstName: 'Amara', lastName: 'Okafor', FirstName: 'Amara', LastName: 'Okafor', status: 'Active', Status: 'Active', depth: 2, coLocationCount: 3, interactionCount: 3, connectionWeight: 0.2, communityId: 0, riskScore: 18 },
      { personId: 2001, peopleId: 2001, PeopleId: 2001, firstName: 'Viktor', lastName: 'Petrov', FirstName: 'Viktor', LastName: 'Petrov', status: 'Non-Compliant', Status: 'Non-Compliant', depth: 1, coLocationCount: 22, interactionCount: 22, connectionWeight: 0.9, communityId: 1, riskScore: 82 },
      { personId: 2002, peopleId: 2002, PeopleId: 2002, firstName: 'Kwame', lastName: 'Asante', FirstName: 'Kwame', LastName: 'Asante', status: 'Absconder', Status: 'Absconder', depth: 1, coLocationCount: 15, interactionCount: 15, connectionWeight: 0.85, communityId: 1, riskScore: 91 },
      { personId: 2003, peopleId: 2003, PeopleId: 2003, firstName: 'Yuki', lastName: 'Tanaka', FirstName: 'Yuki', LastName: 'Tanaka', status: 'Active', Status: 'Active', depth: 2, coLocationCount: 6, interactionCount: 6, connectionWeight: 0.4, communityId: 2, riskScore: 65 },
    ],
  }[p];

  const associationRisk = {
    low:    { associationRiskScore: 0.08, communityRiskLevel: 'low',    totalConnections: 2, highRiskConnections: 0, riskFactors: [] },
    medium: { associationRiskScore: 0.35, communityRiskLevel: 'medium', totalConnections: 3, highRiskConnections: 1, riskFactors: ['highly_connected', '1_high_risk_connections'] },
    high:   { associationRiskScore: 0.82, communityRiskLevel: 'critical', totalConnections: 5, highRiskConnections: 3, riskFactors: ['highly_connected', '3_high_risk_connections', 'absconder_association', 'frequent_co_location'] },
  }[p];

  const centralityMetrics = {
    low:    { degreeCentrality: 0.25, betweennessCentrality: 0.10, pagerank: 0.18, eigenvectorCentrality: 0.22 },
    medium: { degreeCentrality: 0.67, betweennessCentrality: 0.50, pagerank: 0.42, eigenvectorCentrality: 0.55 },
    high:   { degreeCentrality: 0.90, betweennessCentrality: 0.78, pagerank: 0.72, eigenvectorCentrality: 0.85 },
  }[p];

  return {
    personId: pid, focal_person_id: pid,
    totalNodes: cfg.nodes, totalEdges: cfg.edges, communitiesFound: cfg.communities,
    connectedPersons, associationRisk, centralityMetrics,
    builtAt: now(), status: 'completed',
  };
}

export function getDemoNetworkVisualization(alienId, depth = 2) {
  const pid = Number(alienId);
  const p = _profile(alienId);

  if (p === 'low') {
    return {
      nodes: [
        { id: pid, personId: pid, isFocal: true, depth: 0, communityId: 0, connections: 2 },
        { id: 1001, personId: 1001, isFocal: false, depth: 1, communityId: 0, connections: 1 },
        { id: 1002, personId: 1002, isFocal: false, depth: 2, communityId: 0, connections: 1 },
      ],
      links: [
        { source: pid, target: 1001, weight: 0.3, interactionCount: 5 },
        { source: 1001, target: 1002, weight: 0.15, interactionCount: 2 },
      ],
      totalNodes: 3, totalLinks: 2, communitiesFound: 1,
    };
  }

  if (p === 'high') {
    return {
      nodes: [
        { id: pid, personId: pid, isFocal: true, depth: 0, communityId: 0, connections: 4 },
        { id: 1001, personId: 1001, isFocal: false, depth: 1, communityId: 0, connections: 2 },
        { id: 1003, personId: 1003, isFocal: false, depth: 2, communityId: 0, connections: 1 },
        { id: 2001, personId: 2001, isFocal: false, depth: 1, communityId: 1, connections: 3 },
        { id: 2002, personId: 2002, isFocal: false, depth: 1, communityId: 1, connections: 2 },
        { id: 2003, personId: 2003, isFocal: false, depth: 2, communityId: 2, connections: 1 },
      ],
      links: [
        { source: pid, target: 1001, weight: 0.6, interactionCount: 18 },
        { source: pid, target: 2001, weight: 0.9, interactionCount: 22 },
        { source: pid, target: 2002, weight: 0.85, interactionCount: 15 },
        { source: 1001, target: 1003, weight: 0.2, interactionCount: 3 },
        { source: 2001, target: 2002, weight: 0.7, interactionCount: 12 },
        { source: 2001, target: 2003, weight: 0.4, interactionCount: 6 },
        { source: pid, target: 2003, weight: 0.3, interactionCount: 4 },
      ],
      totalNodes: 6, totalLinks: 7, communitiesFound: 3,
    };
  }

  // medium
  return {
    nodes: [
      { id: pid, personId: pid, isFocal: true, depth: 0, communityId: 0, connections: 2 },
      { id: 1001, personId: 1001, isFocal: false, depth: 1, communityId: 0, connections: 2 },
      { id: 1002, personId: 1002, isFocal: false, depth: 1, communityId: 1, connections: 1 },
      { id: 1003, personId: 1003, isFocal: false, depth: 2, communityId: 0, connections: 1 },
    ],
    links: [
      { source: pid, target: 1001, weight: 0.8, interactionCount: 12 },
      { source: pid, target: 1002, weight: 0.5, interactionCount: 8 },
      { source: 1001, target: 1003, weight: 0.3, interactionCount: 5 },
    ],
    totalNodes: 4, totalLinks: 3, communitiesFound: 2,
  };
}

export function getDemoNetworkBuildResult() {
  return { status: 'success', totalNodes: 4, totalEdges: 3, communitiesFound: 2, builtAt: now() };
}

export function getDemoAssociationRisk(alienId) {
  const p = _profile(alienId);

  if (p === 'low') {
    return {
      personId: Number(alienId), associationRiskScore: 0.08, communityRiskLevel: 'low', riskLevel: 'low',
      totalConnections: 2, highRiskConnections: 0,
      riskFactors: [],
      factors: { highRiskConnections: 0, totalConnections: 2, avgConnectionRisk: 10, maxConnectionRisk: 12, networkDensity: 0.3 },
      highRiskAssociates: [],
      calculatedAt: now(),
    };
  }

  if (p === 'high') {
    return {
      personId: Number(alienId), associationRiskScore: 0.82, communityRiskLevel: 'critical', riskLevel: 'critical',
      totalConnections: 5, highRiskConnections: 3,
      riskFactors: ['highly_connected', '3_high_risk_connections', 'absconder_association', 'frequent_co_location'],
      factors: { highRiskConnections: 3, totalConnections: 5, avgConnectionRisk: 61.6, maxConnectionRisk: 91, networkDensity: 0.8 },
      highRiskAssociates: [
        { personId: 2002, name: 'Kwame Asante', riskScore: 91, coLocationCount: 15, lastSeen: daysAgo(3) },
        { personId: 2001, name: 'Viktor Petrov', riskScore: 82, coLocationCount: 22, lastSeen: daysAgo(2) },
        { personId: 2003, name: 'Yuki Tanaka', riskScore: 65, coLocationCount: 6, lastSeen: daysAgo(8) },
      ],
      calculatedAt: now(),
    };
  }

  // medium
  return {
    personId: Number(alienId), associationRiskScore: 0.35, communityRiskLevel: 'medium', riskLevel: 'medium',
    totalConnections: 3, highRiskConnections: 1,
    riskFactors: ['highly_connected', '1_high_risk_connections'],
    factors: { highRiskConnections: 1, totalConnections: 3, avgConnectionRisk: 29.3, maxConnectionRisk: 45, networkDensity: 0.5 },
    highRiskAssociates: [
      { personId: 1002, name: 'David Chen', riskScore: 45, coLocationCount: 8, lastSeen: daysAgo(7) },
    ],
    calculatedAt: now(),
  };
}

// ============================================================================
// HEATMAP
// ============================================================================

function _hexPolygon(lat, lon, radiusDeg = 0.003) {
  const pts = [];
  for (let a = 0; a < 6; a++) {
    const angle = (Math.PI / 3) * a + Math.PI / 6;
    pts.push([lat + radiusDeg * Math.cos(angle), lon + radiusDeg * Math.sin(angle)]);
  }
  pts.push(pts[0]);
  return pts;
}

function _seededRandom(seed) {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

export function getDemoBehavioralHeatmap(alienId) {
  const p = _profile(alienId);
  const cells = [];
  const locations = [
    { lat: DEMO_LOCATIONS.home.lat, lon: DEMO_LOCATIONS.home.lon, weight: 5 },
    { lat: DEMO_LOCATIONS.work.lat, lon: DEMO_LOCATIONS.work.lon, weight: 4 },
    { lat: DEMO_LOCATIONS.gym.lat, lon: DEMO_LOCATIONS.gym.lon, weight: 2 },
    { lat: DEMO_LOCATIONS.grocery.lat, lon: DEMO_LOCATIONS.grocery.lon, weight: 1.5 },
    { lat: DEMO_LOCATIONS.restaurant.lat, lon: DEMO_LOCATIONS.restaurant.lon, weight: 1 },
  ];

  // High risk gets extra hotspots at sketchy locations
  if (p === 'high') {
    locations.push(
      { lat: 27.9756, lon: -82.5333, weight: 3 },   // Airport
      { lat: 27.9350, lon: -82.4100, weight: 3.5 },  // East Tampa
      { lat: 27.9617, lon: -82.4380, weight: 2.5 },  // Ybor
    );
  }

  let cellIdx = 0;
  const cellMultiplier = { low: 0.7, medium: 1.0, high: 1.4 }[p];
  locations.forEach((loc, locIdx) => {
    const nCells = loc.weight > 3 ? 5 : loc.weight > 1.5 ? 3 : 2;
    for (let j = 0; j < nCells; j++) {
      const r = _seededRandom(locIdx * 100 + j);
      const cLat = loc.lat + (r - 0.5) * 0.008;
      const cLon = loc.lon + (_seededRandom(locIdx * 100 + j + 50) - 0.5) * 0.008;
      const activityCount = Math.round(loc.weight * (3 + j * 2) * cellMultiplier);
      cells.push({
        gridCellId: `882a1072b5fffff_${cellIdx}`,
        cellCenterLat: cLat, cellCenterLon: cLon,
        polygon: _hexPolygon(cLat, cLon),
        activityCount,
        visitCount: Math.round(activityCount * 0.6),
        dwellMinutesTotal: Math.round(activityCount * 15 + r * 60),
        timeWeightedScore: parseFloat((loc.weight * (1 + j * 0.3) + r).toFixed(2)),
        firstSeen: daysAgo(28 - cellIdx),
        lastSeen: daysAgo(Math.max(0, cellIdx - 3)),
      });
      cellIdx++;
    }
  });

  return {
    alienId: Number(alienId), resolution: 8, cells,
    summary: {
      totalCells: cells.length,
      totalActivityPoints: cells.reduce((s, c) => s + c.activityCount, 0),
      hotspotCells: cells.filter((c) => c.timeWeightedScore > 3).length,
      generatedAt: now(),
    },
  };
}

export function getDemoTemporalHeatmap(alienId) {
  const baseCells = getDemoBehavioralHeatmap(alienId).cells;
  const slices = [];
  for (let w = 0; w < 4; w++) {
    const sliceCells = baseCells.map((c, i) => ({
      ...c,
      timeWeightedScore: parseFloat((c.timeWeightedScore * (0.6 + _seededRandom(w * 100 + i) * 0.8)).toFixed(2)),
      activityCount: Math.round(c.activityCount * (0.5 + _seededRandom(w * 200 + i) * 1.0)),
    }));
    slices.push({
      sliceLabel: `Week ${w + 1}`,
      sliceStart: daysAgo(28 - w * 7),
      sliceEnd: daysAgo(21 - w * 7),
      totalActivity: sliceCells.reduce((s, c) => s + c.activityCount, 0),
      cells: sliceCells,
    });
  }
  return {
    alienId: Number(alienId), resolution: 8, sliceType: 'weekly', slices,
    totalPoints: slices.reduce((s, sl) => s + sl.totalActivity, 0),
    generatedAt: now(),
  };
}

export function getDemoHeatmapData(personId) {
  return getDemoBehavioralHeatmap(personId);
}

// ============================================================================
// BEHAVIORAL FEATURES
// ============================================================================
export function getDemoBehavioralFeatures(alienId) {
  const p = _profile(alienId);
  const cfg = {
    low: {
      distance: 8.2, locations: 2.5, adherence: 0.94, nightRatio: 0.03,
      weekendRatio: 0.30, zoneAffinity: 0.0, velocityVar: 0.15, dwellEntropy: 0.35,
    },
    medium: {
      distance: 14.3, locations: 3.2, adherence: 0.81, nightRatio: 0.08,
      weekendRatio: 0.45, zoneAffinity: 0.12, velocityVar: 0.35, dwellEntropy: 0.62,
    },
    high: {
      distance: 38.5, locations: 6.8, adherence: 0.32, nightRatio: 0.35,
      weekendRatio: 0.72, zoneAffinity: 0.68, velocityVar: 0.78, dwellEntropy: 0.88,
    },
  }[p];

  return {
    alienId: Number(alienId),
    avgDailyDistanceMiles: cfg.distance,
    uniqueLocationsPerWeek: cfg.locations,
    routineAdherenceScore: cfg.adherence,
    nightActivityRatio: cfg.nightRatio,
    weekendActivityRatio: cfg.weekendRatio,
    highRiskZoneAffinity: cfg.zoneAffinity,
    velocityVariance: cfg.velocityVar,
    dwellTimeEntropy: cfg.dwellEntropy,
    extractionTimestamp: daysAgo(1),
    daysAnalyzed: 30,
  };
}

// ============================================================================
// CLUSTER VISUALIZATION
// ============================================================================
export function getDemoClusterVisualization(alienId) {
  const pid = Number(alienId) || 999;
  const p = _profile(alienId);
  const focalCluster = { low: 0, medium: 1, high: 4 }[p];

  const points = [
    { personId: pid, clusterId: focalCluster, clusterLabel: { low: 'routine_stable', medium: 'mobile_regular', high: 'high_risk_exposure' }[p], x: { low: 1.2, medium: -1.0, high: 2.0 }[p], y: { low: 0.8, medium: 1.5, high: -0.3 }[p] },
    { personId: 210, clusterId: 0, clusterLabel: 'routine_stable', x: 1.5, y: 1.0 },
    { personId: 211, clusterId: 0, clusterLabel: 'routine_stable', x: 0.9, y: 0.6 },
    { personId: 212, clusterId: 1, clusterLabel: 'mobile_regular', x: -1.0, y: 1.5 },
    { personId: 213, clusterId: 1, clusterLabel: 'mobile_regular', x: -0.8, y: 1.8 },
    { personId: 214, clusterId: 2, clusterLabel: 'irregular_patterns', x: 0.5, y: -1.2 },
    { personId: 215, clusterId: 2, clusterLabel: 'irregular_patterns', x: 0.2, y: -1.5 },
    { personId: 216, clusterId: 3, clusterLabel: 'night_active', x: -1.5, y: -0.5 },
    { personId: 217, clusterId: 4, clusterLabel: 'high_risk_exposure', x: 2.0, y: -0.3 },
  ];

  return {
    points,
    clusterCentroids: [
      { clusterId: 0, x: 1.2, y: 0.8 },
      { clusterId: 1, x: -0.9, y: 1.65 },
      { clusterId: 2, x: 0.35, y: -1.35 },
      { clusterId: 3, x: -1.5, y: -0.5 },
      { clusterId: 4, x: 2.0, y: -0.3 },
    ],
    visualizationMethod: 'UMAP',
    totalPoints: points.length,
  };
}

// ============================================================================
// CLUSTER PROFILES & TRAINING
// ============================================================================
export function getDemoClusterProfiles() {
  return [
    { clusterId: 0, clusterLabel: 'routine_stable', memberCount: 3 },
    { clusterId: 1, clusterLabel: 'mobile_regular', memberCount: 2 },
    { clusterId: 2, clusterLabel: 'irregular_patterns', memberCount: 2 },
    { clusterId: 3, clusterLabel: 'night_active', memberCount: 1 },
    { clusterId: 4, clusterLabel: 'high_risk_exposure', memberCount: 1 },
  ];
}

export function getDemoClusterTrainResult() {
  return { status: 'success', nSubjects: 9, nClusters: 5, modelMetrics: { silhouetteScore: 0.72, inertia: 12.4 }, errors: [], trainedAt: now() };
}

// ============================================================================
// NETWORK SUB-COMPONENTS
// ============================================================================
export function getDemoCommunityMembers(communityId) {
  const communities = {
    0: { communityId: 0, label: 'Primary Network', memberCount: 3, members: [
      { personId: 999, name: 'Current Subject', riskScore: 45, role: 'focal' },
      { personId: 1001, name: 'Priya Sharma', riskScore: 28, role: 'associate' },
      { personId: 1003, name: 'Amara Okafor', riskScore: 15, role: 'associate' },
    ]},
    1: { communityId: 1, label: 'Secondary Contact', memberCount: 1, members: [
      { personId: 1002, name: 'David Chen', riskScore: 78, role: 'associate' },
    ]},
    2: { communityId: 2, label: 'Extended Network', memberCount: 2, members: [
      { personId: 2001, name: 'Viktor Petrov', riskScore: 82, role: 'associate' },
      { personId: 2003, name: 'Yuki Tanaka', riskScore: 65, role: 'associate' },
    ]},
  };
  return communities[communityId] || communities[0];
}

export function getDemoEdgeDetails(person1Id, person2Id) {
  return {
    person1Id: Number(person1Id), person2Id: Number(person2Id),
    coLocationCount: 8, weight: 8, firstSeen: daysAgo(45), lastSeen: daysAgo(7),
    sharedLocations: [
      { latitude: DEMO_LOCATIONS.work.lat, longitude: DEMO_LOCATIONS.work.lon, name: 'Workplace - Downtown Tampa', count: 5 },
      { latitude: DEMO_LOCATIONS.restaurant.lat, longitude: DEMO_LOCATIONS.restaurant.lon, name: 'Ybor City Restaurant', count: 3 },
    ],
    temporalOverlap: 0.35, avgProximityMeters: 120,
  };
}

// ============================================================================
// PERSON INFO
// ============================================================================
export function getDemoPersonInfo(personId) {
  const people = {
    1001: { id: 1001, peopleId: 1001, PeopleId: 1001, firstName: 'Priya', lastName: 'Sharma', FirstName: 'Priya', LastName: 'Sharma', fullName: 'Priya Sharma', status: 'Active', Status: 'Active' },
    1002: { id: 1002, peopleId: 1002, PeopleId: 1002, firstName: 'David', lastName: 'Chen', FirstName: 'David', LastName: 'Chen', fullName: 'David Chen', status: 'Active', Status: 'Active' },
    1003: { id: 1003, peopleId: 1003, PeopleId: 1003, firstName: 'Amara', lastName: 'Okafor', FirstName: 'Amara', LastName: 'Okafor', fullName: 'Amara Okafor', status: 'Compliant', Status: 'Compliant' },
    2001: { id: 2001, peopleId: 2001, PeopleId: 2001, firstName: 'Viktor', lastName: 'Petrov', FirstName: 'Viktor', LastName: 'Petrov', fullName: 'Viktor Petrov', status: 'Non-Compliant', Status: 'Non-Compliant' },
    2002: { id: 2002, peopleId: 2002, PeopleId: 2002, firstName: 'Kwame', lastName: 'Asante', FirstName: 'Kwame', LastName: 'Asante', fullName: 'Kwame Asante', status: 'Absconder', Status: 'Absconder' },
    2003: { id: 2003, peopleId: 2003, PeopleId: 2003, firstName: 'Yuki', lastName: 'Tanaka', FirstName: 'Yuki', LastName: 'Tanaka', fullName: 'Yuki Tanaka', status: 'Active', Status: 'Active' },
  };
  const pid = Number(personId);
  return people[pid] || { id: pid, peopleId: pid, PeopleId: pid, firstName: 'Subject', lastName: String(personId), FirstName: 'Subject', LastName: String(personId), fullName: `Subject ${personId}`, status: 'Active', Status: 'Active' };
}

// ============================================================================
// CASE SUMMARY
// ============================================================================
export function getDemoCaseSummary(peopleId) {
  const p = _profile(peopleId);

  const names = { low: 'Amara Okafor', medium: 'Priya Sharma', high: 'David Chen' };

  const cfg = {
    low: {
      name: names.low, aNumber: 'A045-678-901',
      complianceStatus: 'Compliant', bondType: 'Bond', bondAmount: 10000, bondStatus: 'Active',
      checkIns: { totalCheckIns: 24, completedCheckIns: 24, missedCheckIns: 0, pendingCheckIns: 0, completionRate: 100, averageResponseTimeMinutes: 5.2 },
      risk: { initialRiskLevel: 'Low', currentRiskLevel: 'Low', riskTrend: 'stable', riskAlertCount: 0 },
      location: { geofenceViolationCount: 0, curfewViolationCount: 0, gpsAnomalyCount: 0, travel: { totalLocationPings: 2240, percentTimeWithinGeofence: 99.8, maxDistanceFromResidenceMiles: 12.1, statesVisited: ['FL'] } },
      court: { totalCourtEvents: 2, appearedCount: 2, noShowCount: 0, pendingCount: 0 },
      payments: { totalDue: 1000, totalPaid: 1000, outstanding: 0, paymentCompletionRate: 100 },
    },
    medium: {
      name: names.medium, aNumber: 'A012-345-678',
      complianceStatus: 'Compliant', bondType: 'Bond', bondAmount: 15000, bondStatus: 'Active',
      checkIns: { totalCheckIns: 24, completedCheckIns: 22, missedCheckIns: 2, pendingCheckIns: 1, completionRate: 91.7, averageResponseTimeMinutes: 12.4 },
      risk: { initialRiskLevel: 'Medium', currentRiskLevel: 'Medium', riskTrend: 'stable', riskAlertCount: 3 },
      location: { geofenceViolationCount: 1, curfewViolationCount: 0, gpsAnomalyCount: 2, travel: { totalLocationPings: 1842, percentTimeWithinGeofence: 97.3, maxDistanceFromResidenceMiles: 28.4, statesVisited: ['FL'] } },
      court: { totalCourtEvents: 3, appearedCount: 2, noShowCount: 0, pendingCount: 1 },
      payments: { totalDue: 1500, totalPaid: 1200, outstanding: 300, paymentCompletionRate: 80 },
    },
    high: {
      name: names.high, aNumber: 'A078-912-345',
      complianceStatus: 'Non-Compliant', bondType: 'Bond', bondAmount: 25000, bondStatus: 'Active',
      checkIns: { totalCheckIns: 24, completedCheckIns: 16, missedCheckIns: 7, pendingCheckIns: 1, completionRate: 66.7, averageResponseTimeMinutes: 38.6 },
      risk: { initialRiskLevel: 'Medium', currentRiskLevel: 'Critical', riskTrend: 'increasing', riskAlertCount: 12 },
      location: { geofenceViolationCount: 8, curfewViolationCount: 5, gpsAnomalyCount: 9, travel: { totalLocationPings: 3120, percentTimeWithinGeofence: 72.4, maxDistanceFromResidenceMiles: 85.3, statesVisited: ['FL', 'GA'] } },
      court: { totalCourtEvents: 3, appearedCount: 1, noShowCount: 1, pendingCount: 1 },
      payments: { totalDue: 2500, totalPaid: 1500, outstanding: 1000, paymentCompletionRate: 60 },
    },
  }[p];

  return {
    peopleId: Number(peopleId),
    defendantName: cfg.name,
    aNumber: cfg.aNumber,
    dateOfBirth: { low: '1995-03-22', medium: '1990-06-15', high: '1988-11-08' }[p],
    supervisionStartDate: daysAgo(90),
    supervisionEndDate: null,
    supervisionDurationDays: 90,
    complianceStatus: cfg.complianceStatus,
    caseOutcome: null,
    bondType: cfg.bondType,
    bondAmount: cfg.bondAmount,
    bondStatus: cfg.bondStatus,
    generatedAt: now(),
    checkIns: cfg.checkIns,
    risk: cfg.risk,
    locationData: cfg.location,
    courtEvents: cfg.court,
    payments: cfg.payments,
  };
}

// ============================================================================
// PEOPLE LIST
// ============================================================================
export function getDemoPeopleList() {
  return [
    { peopleId: 1003, alienId: 1003, firstName: 'Amara', lastName: 'Okafor', peopleName: 'Amara Okafor', alienName: 'Amara Okafor' },
    { peopleId: 1001, alienId: 1001, firstName: 'Priya', lastName: 'Sharma', peopleName: 'Priya Sharma', alienName: 'Priya Sharma' },
    { peopleId: 1002, alienId: 1002, firstName: 'David', lastName: 'Chen', peopleName: 'David Chen', alienName: 'David Chen' },
  ];
}

export function getDemoPersonBatch(personIds) {
  return personIds.map((id) => getDemoPersonInfo(id));
}
